<template>
  <div class="p-4 lg:p-6">
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow p-4 lg:p-6">
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4 ">Configuração do HelpDesk</h2>
      
      <div class="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            @click="currentTab = tab.key"
            :class="[
              currentTab === tab.key
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors'
            ]"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <div v-if="currentTab === 'filas'">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-3">Filas de Suporte</h3>
        <form @submit.prevent="addFila" class="mb-4 flex items-center space-x-2">
          <input
            v-model="newFilaName"
            type="text"
            placeholder="Nome da nova fila"
            class="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            required
          />
          <button
            type="submit"
            :disabled="filaFormLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            Adicionar Fila
          </button>
        </form>
        <div v-if="filaFormError" class="text-red-600 dark:text-red-400 mb-2 text-sm">{{ filaFormError }}</div>
        <div v-if="loadingFilas" class="text-gray-500 dark:text-gray-300">Carregando...</div>
        <div v-else class="space-y-2">
          <div v-for="fila in filas" :key="fila.id" class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <span class="text-sm text-gray-800 dark:text-gray-100 font-medium">{{ fila.nome }}</span>
            <div class="flex space-x-2">
              <button @click="openEditModal(fila)" class="px-3 py-1 text-xs font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Editar</button>
              <button @click="deleteFila(fila.id)" class="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Excluir</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentTab === 'sistemas'">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-3">Sistemas</h3>
        <form @submit.prevent="saveSistema" class="mb-4 flex items-center space-x-2">
          <input v-model="sistemaForm.nome" type="text" placeholder="Nome do sistema" class="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
          <button type="submit" :disabled="sistemaFormLoading" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">{{ sistemaFormMode === 'add' ? 'Adicionar' : 'Salvar' }}</button>
          <button v-if="sistemaFormMode === 'edit'" type="button" @click="cancelSistemaEdit" class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">Cancelar</button>
        </form>
        <div v-if="sistemaFormError" class="text-red-600 dark:text-red-400 mb-2 text-sm">{{ sistemaFormError }}</div>
        <div v-if="loadingSistemas" class="text-gray-500 dark:text-gray-300">Carregando...</div>
        <div v-else class="space-y-2">
          <div v-for="sistema in sistemas" :key="sistema.id" class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <span class="text-sm text-gray-800 dark:text-gray-100 font-medium">{{ sistema.nome }}</span>
            <div class="flex space-x-2">
              <button @click="editSistema(sistema)" class="px-3 py-1 text-xs font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Editar</button>
              <button @click="deleteSistema(sistema.id)" class="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Excluir</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentTab === 'tipos'">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-3">Tipos de Chamado</h3>
        <form @submit.prevent="saveTipo" class="mb-4 space-y-2 md:space-y-0 md:flex md:items-end md:space-x-2">
          <input v-model="tipoForm.nome" type="text" placeholder="Nome do tipo" class="w-full md:w-auto flex-grow px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
          <input v-model="tipoForm.descricao" type="text" placeholder="Descrição" class="w-full md:w-auto flex-grow px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          <button type="submit" :disabled="tipoFormLoading" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">{{ tipoFormMode === 'add' ? 'Adicionar' : 'Salvar' }}</button>
          <button v-if="tipoFormMode === 'edit'" type="button" @click="cancelTipoEdit" class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">Cancelar</button>
        </form>
         <div v-if="tipoFormError" class="text-red-600 dark:text-red-400 mb-2 text-sm">{{ tipoFormError }}</div>
        <div v-if="loadingTipos" class="text-gray-500 dark:text-gray-300">Carregando...</div>
        <div v-else class="space-y-2">
          <div v-for="tipo in tipos" :key="tipo.id" class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div>
              <span class="text-sm text-gray-800 dark:text-gray-100 font-medium">{{ tipo.nome }}</span>
              <span class="text-xs text-gray-500 dark:text-gray-300 ml-2">{{ tipo.descricao }}</span>
            </div>
            <div class="flex space-x-2">
              <button @click="editTipo(tipo)" class="px-3 py-1 text-xs font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Editar</button>
              <button @click="deleteTipo(tipo.id)" class="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Excluir</button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="currentTab === 'slas'">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-3">SLAs por Tipo de Chamado</h3>
        <form @submit.prevent="saveSla" class="mb-4 space-y-2 md:space-y-0 md:flex md:items-end md:space-x-2">
            <select v-model="slaForm.tipo_id" required class="w-full md:w-auto px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                <option value="">Selecione o tipo</option>
                <option v-for="tipo in tipos" :key="tipo.id" :value="tipo.id">{{ tipo.nome }}</option>
            </select>
            <input v-model.number="slaForm.tempo_primeira_resposta" type="number" min="0" placeholder="1ª Resp. (h)" class="w-full md:w-32 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
            <input v-model.number="slaForm.tempo_resolucao" type="number" min="0" placeholder="Resolução (h)" class="w-full md:w-32 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
            <button type="submit" :disabled="slaFormLoading" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">{{ slaFormMode === 'add' ? 'Adicionar' : 'Salvar' }}</button>
            <button v-if="slaFormMode === 'edit'" type="button" @click="cancelSlaEdit" class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">Cancelar</button>
        </form>
        <div v-if="slaFormError" class="text-red-600 dark:text-red-400 mb-2 text-sm">{{ slaFormError }}</div>
        <div v-if="loadingSlas" class="text-gray-500 dark:text-gray-300">Carregando...</div>
        <div v-else class="space-y-2">
            <div v-for="sla in slas" :key="sla.id" class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div>
                    <span class="text-sm text-gray-800 dark:text-gray-100 font-medium">{{ sla.tipo_nome }}</span>
                    <span class="text-xs text-gray-500 dark:text-gray-300 ml-2">1ª Resposta: {{ sla.tempo_primeira_resposta }}h, Resolução: {{ sla.tempo_resolucao }}h</span>
                </div>
                <div class="flex space-x-2">
                    <button @click="editSla(sla)" class="px-3 py-1 text-xs font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Editar</button>
                    <button @click="deleteSla(sla.id)" class="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Excluir</button>
                </div>
            </div>
        </div>
      </div>

    </div>
  </div>

  <div v-if="isEditModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" @click.self="closeEditModal">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
      <form @submit.prevent="saveFilaChanges">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-800">Editar Fila</h3>
          <button type="button" @click="closeEditModal" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label for="fila-name" class="block text-sm font-medium text-gray-700">Nome da Fila</label>
            <input id="fila-name" v-model="editingFila.nome" type="text" class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" required />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Vincular Usuários (TI)</label>
            <div v-if="loadingUsers" class="text-sm text-gray-500">Carregando usuários...</div>
            <div v-else class="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
              <label v-for="user in tiUsers" :key="user.id" class="flex items-center space-x-3 cursor-pointer p-1 rounded hover:bg-gray-100">
                <input type="checkbox" :value="user.id" v-model="selectedUserIds" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span class="text-sm text-gray-700">{{ user.nome }}</span>
              </label>
            </div>
          </div>
           <div v-if="filaFormError" class="text-red-600 text-sm">{{ filaFormError }}</div>
        </div>
        <div class="px-6 py-3 bg-gray-50 flex justify-end space-x-3">
          <button type="button" @click="closeEditModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button type="submit" :disabled="filaFormLoading" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">Salvar Alterações</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import apiClient from '@/services/authService';

// --- Estado das Abas ---
const tabs = [
  { key: 'filas', label: 'Filas de Suporte' },
  { key: 'sistemas', label: 'Sistemas' },
  { key: 'tipos', label: 'Tipos de Chamado' },
  { key: 'slas', label: 'SLAs' },
];
const currentTab = ref('filas');

// --- Estado da Aba Filas ---
const filas = ref([]);
const loadingFilas = ref(true);
const errorFilas = ref(null);
const newFilaName = ref('');
const filaFormLoading = ref(false);
const filaFormError = ref(null);

// --- Estado do Modal de Edição de Filas ---
const isEditModalVisible = ref(false);
const editingFila = ref(null);
const selectedUserIds = ref([]);
const originalUserIds = ref([]);
const tiUsers = ref([]);
const loadingUsers = ref(true);

// --- Estado da Aba Sistemas ---
const sistemas = ref([]);
const loadingSistemas = ref(true);
const errorSistemas = ref(null);
const sistemaForm = ref({ nome: '', id: null });
const sistemaFormMode = ref('add');
const sistemaFormLoading = ref(false);
const sistemaFormError = ref(null);

// --- Estado da Aba Tipos de Chamado ---
const tipos = ref([]);
const loadingTipos = ref(true);
const errorTipos = ref(null);
const tipoForm = ref({ nome: '', descricao: '', id: null });
const tipoFormMode = ref('add');
const tipoFormLoading = ref(false);
const tipoFormError = ref(null);

// --- Estado da Aba SLAs ---
const slas = ref([]);
const loadingSlas = ref(true);
const errorSlas = ref(null);
const slaForm = ref({ tipo_id: '', tempo_primeira_resposta: '', tempo_resolucao: '', id: null });
const slaFormMode = ref('add');
const slaFormLoading = ref(false);
const slaFormError = ref(null);

// --- Carregamento Inicial ---
onMounted(async () => {
  await Promise.all([
    loadFilas(),
    loadTIUsers(),
    loadSistemas(),
    loadTipos(),
  ]).then(() => {
    // SLAs dependem dos Tipos, então carregue depois
    loadSlas();
  });
});

// --- Funções da Aba Filas ---
async function loadFilas() {
  loadingFilas.value = true;
  errorFilas.value = null;
  try {
    const { data } = await apiClient.get('/api/v1/tickets/queues/');
    filas.value = data;
  } catch (e) {
    errorFilas.value = e.response?.data?.detail || 'Erro ao carregar filas.';
  } finally {
    loadingFilas.value = false;
  }
}
async function addFila() {
  if (!newFilaName.value.trim()) return;
  filaFormLoading.value = true;
  filaFormError.value = null;
  try {
    await apiClient.post('/api/v1/tickets/queues/', { nome: newFilaName.value });
    newFilaName.value = '';
    await loadFilas();
  } catch (e) {
    filaFormError.value = e.response?.data?.detail || 'Erro ao adicionar fila.';
  } finally {
    filaFormLoading.value = false;
  }
}
async function deleteFila(filaId) {
  if (!confirm('Tem certeza que deseja excluir esta fila?')) return;
  try {
    await apiClient.delete(`/api/v1/tickets/queues/${filaId}/`);
    await loadFilas();
  } catch (e) {
    alert(e.response?.data?.detail || 'Erro ao excluir a fila.');
  }
}
async function openEditModal(fila) {
  editingFila.value = { ...fila };
  isEditModalVisible.value = true;
  try {
    const { data } = await apiClient.get(`/api/v1/tickets/fila-users/?fila_id=${fila.id}`);
    const userIds = data.map(user => user.id);
    selectedUserIds.value = userIds;
    originalUserIds.value = [...userIds];
  } catch (e) {
    console.error("Erro ao buscar usuários da fila:", e);
    alert("Não foi possível carregar os usuários desta fila.");
    closeEditModal();
  }
}
function closeEditModal() {
  isEditModalVisible.value = false;
  editingFila.value = null;
  selectedUserIds.value = [];
  originalUserIds.value = [];
  filaFormError.value = null;
}
async function saveFilaChanges() {
  filaFormLoading.value = true;
  filaFormError.value = null;
  try {
    const originalFila = filas.value.find(f => f.id === editingFila.value.id);
    if (originalFila && originalFila.nome !== editingFila.value.nome) {
      await apiClient.put(`/api/v1/tickets/queues/${editingFila.value.id}/`, { nome: editingFila.value.nome });
    }
    const originalSet = new Set(originalUserIds.value);
    const selectedSet = new Set(selectedUserIds.value);
    const usersToAdd = [...selectedSet].filter(id => !originalSet.has(id));
    const usersToRemove = [...originalSet].filter(id => !selectedSet.has(id));
    const promises = [];
    usersToAdd.forEach(userId => {
      promises.push(apiClient.post('/api/v1/tickets/fila-users/', { user_id: userId, fila_id: editingFila.value.id }));
    });
    usersToRemove.forEach(userId => {
      promises.push(apiClient.delete('/api/v1/tickets/fila-users/', { data: { user_id: userId, fila_id: editingFila.value.id } }));
    });
    await Promise.all(promises);
    closeEditModal();
    await loadFilas();
  } catch (e) {
    console.error("Erro ao salvar alterações na fila:", e);
    filaFormError.value = e.response?.data?.detail || "Erro ao salvar as alterações.";
  } finally {
    filaFormLoading.value = false;
  }
}
async function loadTIUsers() {
  loadingUsers.value = true;
  try {
    const { data: allUsers } = await apiClient.get('/api/v1/auth/users');
    tiUsers.value = allUsers
      .filter(user => user.groups && user.groups.includes('ti'))
      .map(user => ({
        id: user.id,
        nome: `${user.first_name} ${user.last_name}`.trim() || user.username,
        first_name: user.first_name,
        last_name: user.last_name
      }));
  } catch (e) {
    console.error('Erro ao carregar usuários de TI:', e);
  } finally {
    loadingUsers.value = false;
  }
}

// --- Funções da Aba Sistemas ---
async function loadSistemas() {
  loadingSistemas.value = true;
  errorSistemas.value = null;
  try {
    const { data } = await apiClient.get('/api/v1/tickets/systems/');
    sistemas.value = data;
  } catch (e) {
    errorSistemas.value = e.response?.data?.detail || 'Erro ao carregar sistemas.';
  } finally {
    loadingSistemas.value = false;
  }
}
async function saveSistema() {
  sistemaFormLoading.value = true;
  sistemaFormError.value = null;
  try {
    const payload = { nome: sistemaForm.value.nome };
    if (sistemaFormMode.value === 'add') {
      await apiClient.post('/api/v1/tickets/systems/', payload);
    } else {
      await apiClient.put(`/api/v1/tickets/systems/${sistemaForm.value.id}/`, payload);
    }
    cancelSistemaEdit();
    await loadSistemas();
  } catch (e) {
    sistemaFormError.value = e.response?.data?.detail || 'Erro ao salvar sistema.';
  } finally {
    sistemaFormLoading.value = false;
  }
}
function editSistema(sistema) {
  sistemaForm.value = { ...sistema };
  sistemaFormMode.value = 'edit';
}
function cancelSistemaEdit() {
  sistemaForm.value = { nome: '', id: null };
  sistemaFormMode.value = 'add';
  sistemaFormError.value = null;
}
async function deleteSistema(sistemaId) {
  if (!confirm('Tem certeza?')) return;
  try {
    await apiClient.delete(`/api/v1/tickets/systems/${sistemaId}/`);
    await loadSistemas();
  } catch (e) {
    alert(e.response?.data?.detail || 'Erro ao excluir sistema.');
  }
}

// --- Funções da Aba Tipos ---
async function loadTipos() {
  loadingTipos.value = true;
  errorTipos.value = null;
  try {
    const { data } = await apiClient.get('/api/v1/tickets/tipos/');
    tipos.value = data;
  } catch (e) {
    errorTipos.value = e.response?.data?.detail || 'Erro ao carregar tipos.';
  } finally {
    loadingTipos.value = false;
  }
}
async function saveTipo() {
  tipoFormLoading.value = true;
  tipoFormError.value = null;
  try {
    const payload = { nome: tipoForm.value.nome, descricao: tipoForm.value.descricao };
    if (tipoFormMode.value === 'add') {
      await apiClient.post('/api/v1/tickets/tipos/', payload);
    } else {
      await apiClient.put(`/api/v1/tickets/tipos/${tipoForm.value.id}/`, payload);
    }
    cancelTipoEdit();
    await loadTipos();
  } catch (e) {
    tipoFormError.value = e.response?.data?.detail || 'Erro ao salvar tipo.';
  } finally {
    tipoFormLoading.value = false;
  }
}
function editTipo(tipo) {
  tipoForm.value = { ...tipo };
  tipoFormMode.value = 'edit';
}
function cancelTipoEdit() {
  tipoForm.value = { nome: '', descricao: '', id: null };
  tipoFormMode.value = 'add';
  tipoFormError.value = null;
}
async function deleteTipo(tipoId) {
  if (!confirm('Tem certeza?')) return;
  try {
    await apiClient.delete(`/api/v1/tickets/tipos/${tipoId}/`);
    await loadTipos();
  } catch (e) {
    alert(e.response?.data?.detail || 'Erro ao excluir tipo.');
  }
}

// --- Funções da Aba SLAs ---
async function loadSlas() {
  loadingSlas.value = true;
  errorSlas.value = null;
  try {
    const { data } = await apiClient.get('/api/v1/tickets/slas/');
    slas.value = data;
  } catch (e) {
    errorSlas.value = e.response?.data?.detail || 'Erro ao carregar SLAs.';
  } finally {
    loadingSlas.value = false;
  }
}
async function saveSla() {
  slaFormLoading.value = true;
  slaFormError.value = null;
  try {
    const payload = {
      tipo_id: slaForm.value.tipo_id,
      tempo_primeira_resposta: slaForm.value.tempo_primeira_resposta,
      tempo_resolucao: slaForm.value.tempo_resolucao
    };
    if (slaFormMode.value === 'add') {
      await apiClient.post('/api/v1/tickets/slas/', payload);
    } else {
      await apiClient.put(`/api/v1/tickets/slas/${slaForm.value.id}/`, payload);
    }
    cancelSlaEdit();
    await loadSlas();
  } catch (e) {
    slaFormError.value = e.response?.data?.detail || 'Erro ao salvar SLA.';
  } finally {
    slaFormLoading.value = false;
  }
}
function editSla(sla) {
  slaForm.value = { ...sla };
  slaFormMode.value = 'edit';
}
function cancelSlaEdit() {
  slaForm.value = { tipo_id: '', tempo_primeira_resposta: '', tempo_resolucao: '', id: null };
  slaFormMode.value = 'add';
  slaFormError.value = null;
}
async function deleteSla(slaId) {
  if (!confirm('Tem certeza?')) return;
  try {
    await apiClient.delete(`/api/v1/tickets/slas/${slaId}/`);
    await loadSlas();
  } catch (e) {
    alert(e.response?.data?.detail || 'Erro ao excluir SLA.');
  }
}

</script>
